import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { PlayCircle, Music, Trees, X } from 'lucide-react';
import Navigation from '../components/Navigation';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Videos() {
    const [videos, setVideos] = useState([]);
    const [category, setCategory] = useState('all');
    const [loading, setLoading] = useState(true);
    const [activeVideo, setActiveVideo] = useState(null);
    const videoPlayerRef = useRef(null);

    useEffect(() => {
        fetchVideos();
    }, [category]);

    const fetchVideos = async () => {
        try {
            const url = category === 'all' 
                ? `${API_URL}/videos` 
                : `${API_URL}/videos?category=${category}`;
            const response = await axios.get(url);
            setVideos(response.data);
        } catch (error) {
            console.error('Failed to load videos:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleVideoClick = (video) => {
        setActiveVideo(video);
        // Scroll to video player after a short delay
        setTimeout(() => {
            videoPlayerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
    };

    const closeVideo = () => {
        setActiveVideo(null);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center">
                <div className="loading-spinner" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#FDFCF8]" data-testid="videos-page">
            <Navigation />
            
            <main className="container-app py-8 md:py-12">
                {/* Header */}
                <div className="mb-10 animate-fade-in">
                    <div className="flex items-center gap-2 mb-4">
                        <PlayCircle className="w-6 h-6 text-primary" />
                        <span className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">
                            Inspirational Media
                        </span>
                    </div>
                    <h1 className="font-display text-3xl md:text-4xl font-medium text-[#2D2A26] mb-2">
                        Peaceful Videos
                    </h1>
                    <p className="text-muted-foreground">
                        Relax and find peace with calming nature scenes and worship music
                    </p>
                </div>

                {/* Category Tabs */}
                <div className="mb-8">
                    <Tabs value={category} onValueChange={setCategory}>
                        <TabsList className="bg-muted/50">
                            <TabsTrigger value="all" data-testid="all-videos-tab">
                                All
                            </TabsTrigger>
                            <TabsTrigger value="nature" data-testid="nature-videos-tab">
                                <Trees className="w-4 h-4 mr-2" />
                                Nature
                            </TabsTrigger>
                            <TabsTrigger value="worship" data-testid="worship-videos-tab">
                                <Music className="w-4 h-4 mr-2" />
                                Worship
                            </TabsTrigger>
                        </TabsList>
                    </Tabs>
                </div>

                {/* Featured Video Player */}
                {activeVideo && (
                    <div ref={videoPlayerRef} className="mb-10 animate-fade-in" data-testid="featured-video">
                        <div className="relative">
                            <button 
                                onClick={closeVideo}
                                className="absolute -top-3 -right-3 z-10 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-100 transition-colors"
                                data-testid="close-video-btn"
                            >
                                <X className="w-5 h-5 text-gray-600" />
                            </button>
                            <div className="video-wrapper shadow-float rounded-3xl overflow-hidden">
                                <iframe
                                    src={`https://www.youtube.com/embed/${activeVideo.id}?autoplay=1&rel=0`}
                                    title={activeVideo.title}
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                    className="w-full aspect-video"
                                />
                            </div>
                        </div>
                        <h3 className="font-display text-xl text-[#2D2A26] mt-4">
                            {activeVideo.title}
                        </h3>
                    </div>
                )}

                {/* Video Grid */}
                <div className="video-grid">
                    {videos.map((video, index) => (
                        <button 
                            key={video.id}
                            type="button"
                            className={`group text-left animate-fade-in stagger-${Math.min(index + 1, 6)} ${
                                activeVideo?.id === video.id ? 'ring-2 ring-primary ring-offset-2 rounded-3xl' : ''
                            }`}
                            onClick={() => handleVideoClick(video)}
                            data-testid={`video-card-${index}`}
                        >
                            <div className="video-wrapper relative overflow-hidden rounded-3xl">
                                <img
                                    src={`https://img.youtube.com/vi/${video.id}/maxresdefault.jpg`}
                                    alt={video.title}
                                    className="w-full aspect-video object-cover transition-transform duration-500 group-hover:scale-105"
                                    onError={(e) => {
                                        e.target.src = `https://img.youtube.com/vi/${video.id}/hqdefault.jpg`;
                                    }}
                                />
                                <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                    <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                                        <PlayCircle className="w-8 h-8 text-primary" />
                                    </div>
                                </div>
                                <div className="absolute top-3 right-3">
                                    <span className={`text-xs px-3 py-1 rounded-full ${
                                        video.category === 'worship' 
                                            ? 'bg-primary/90 text-white' 
                                            : 'bg-accent/90 text-white'
                                    }`}>
                                        {video.category === 'worship' ? (
                                            <span className="flex items-center gap-1">
                                                <Music className="w-3 h-3" /> Worship
                                            </span>
                                        ) : (
                                            <span className="flex items-center gap-1">
                                                <Trees className="w-3 h-3" /> Nature
                                            </span>
                                        )}
                                    </span>
                                </div>
                            </div>
                            <h3 className="font-medium text-[#2D2A26] mt-3 group-hover:text-primary transition-colors">
                                {video.title}
                            </h3>
                        </button>
                    ))}
                </div>

                {videos.length === 0 && (
                    <div className="text-center py-16">
                        <PlayCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                        <h3 className="font-display text-xl text-[#2D2A26] mb-2">
                            No videos found
                        </h3>
                        <p className="text-muted-foreground">
                            Try selecting a different category
                        </p>
                    </div>
                )}
            </main>
        </div>
    );
}
